import Swal from 'sweetalert2';
import { ReturnCodes } from '../utils/constants';
import localVideoUploadServiceInstance from './LocalVideoUploadService';
import videoCRUDServiceInstance from './VideoCRUDService';
/**
 * Helper service for video upload functionality
 * Consolidates the common upload logic used across different components
 */
class VideoUploadService {
    constructor() {
        this.trackingFunction = null;
    }

    // Method to be called from a component to set the tracking function
    setTrackingFunction(trackFn) {
        this.trackingFunction = trackFn;
    }

    /**
     * Handles video upload including validation and API interactions
     * @param {string} linkType - The type of link (GOOGLE_DRIVE, YOUTUBE, LOCAL_FILE)
     * @param {Object} params - Upload parameters
     * @returns {Promise} Promise resolving to upload result
     */
    async uploadVideo(linkType, params) {
        const {
            setFileUploading,
            googleLinkURL,
            youtubeLinkURL,
            videoName,
            folderPath,
            isCompressed,
            selectOpenAI,
            isLogging,
            skipTranslationIsChecked,
            onlyTransliterationIsChecked,
            bitRate,
            crfValue,
            setOpen,
            onSuccess,
            selectedFile,
            subtitleFile,
        } = params;

        // If subtitle file is provided, use the uploadVideoWithSubtitles flow
        if (subtitleFile) {
            // Get target languages from localStorage and process them
            const selectedVideoLanguages = JSON.parse(localStorage.getItem('createVideoLanguages'));
            let targetLanguages = '';
            let sourceLanguage = 'English';

            if (selectedVideoLanguages && selectedVideoLanguages.length > 0) {
                // Filter out English (source language) from target languages
                // also remove 'guj'
                const filteredLanguages = selectedVideoLanguages.filter(
                    (lang) => lang !== 'Gujarati' && lang !== 'Hindi',
                );
                targetLanguages = filteredLanguages.join(',');
            }

            return this.uploadVideoWithSubtitles(linkType, {
                setFileUploading,
                googleLinkURL,
                youtubeLinkURL,
                videoName,
                folderPath,
                isCompressed,
                subtitleFile,
                sourceLanguage,
                targetLanguages,
                setOpen,
                onSuccess,
                selectedFile,
            });
        }

        setFileUploading(true);
        let url = '';
        let videoNameUpdated = videoName;
        if (linkType === 'GOOGLE_DRIVE') {
            url = googleLinkURL;
        } else if (linkType === 'YOUTUBE') {
            url = youtubeLinkURL;
        } else if (linkType === 'LOCAL_FILE') {
            try {
                url = await localVideoUploadServiceInstance.uploadFile(selectedFile);
                console.log('Local file upload response:', url);
                url = url?.data;
                // url?.data is the server-stored path (e.g. filename_abc12.mp4)
                // Keep videoNameUpdated as the user-provided videoName for readability at the AI server
            } catch (error) {
                console.error('Error uploading local file:', error);
                setFileUploading(false);
                return;
            }
        }

        try {
            if (videoNameUpdated.length === 0) {
                setFileUploading(false);
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Please enter a video name',
                });
                return;
            }

            return videoCRUDServiceInstance
                .addVideoInFolderWithPromise(
                    linkType,
                    url,
                    JSON.parse(localStorage.getItem('authUser')),
                    videoNameUpdated,
                    folderPath,
                    isCompressed,
                    selectOpenAI,
                    isLogging,
                    skipTranslationIsChecked,
                    onlyTransliterationIsChecked,
                    bitRate,
                    crfValue,
                )
                .then((result) => {
                    setFileUploading(false);
                    if (setOpen) setOpen(false);

                    console.log('Upload result:', result);

                    if (result && (result.status === 'FILE_UPLOAD_SUCCESS' || result.id || result.videoId)) {
                        const videoId = result.id || result.videoId || result._id;

                        if (this.trackingFunction && videoId) {
                            console.log('Tracking uploaded video:', videoId, videoName);
                            this.trackingFunction(videoId, videoName + ' - New Video Upload', skipTranslationIsChecked);
                        } else {
                            console.warn(
                                'Cannot track video: ',
                                !this.trackingFunction ? 'No tracking function available' : 'No video ID returned',
                            );
                        }

                        Swal.fire({
                            icon: 'success',
                            title: 'Video Uploaded Successfully',
                            html: `Your video <strong>${videoName}</strong> is now being processed.<br><br>
               You can track its progress in the toast notifications in the bottom right corner.<br><br>
               For detailed status and progress information, visit the <a href="/status/processing-status" style="color: blue; text-decoration: underline;" target="_blank" rel="noopener noreferrer">Processing Status Dashboard</a>.`,
                            timer: 5000,
                        });

                        if (onSuccess && typeof onSuccess === 'function') {
                            onSuccess(videoId);
                        }
                        return true;
                    } else {
                        Swal.fire({
                            icon: 'error',
                            title: 'Error',
                            text: 'Failed to upload video. Please try again.',
                        });
                        return false;
                    }
                })
                .catch((error) => {
                    console.error('Error in video upload:', error);
                    setFileUploading(false);
                    Swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: 'Failed to upload video',
                    });
                    return false;
                });
        } catch (err) {
            console.error('Exception in uploadVideo:', err);
            setFileUploading(false);
            return false;
        }
    }

    /**
     * Show detailed process information modal
     * @param {string} linkType - The type of link
     * @param {string} videoName - The name of the video
     * @param {boolean} skipTranslationIsChecked - Whether translation is skipped
     */
    showUploadProcessInfo(linkType, videoName, skipTranslationIsChecked) {
        Swal.fire({
            icon: 'info',
            title: 'Upload Process Information',
            html: `
        <p>Link Type: ${linkType}</p>
        <p>Video Name: ${videoName}</p>
        <p>Skip Translation: ${skipTranslationIsChecked}</p>
      `,
            showConfirmButton: true,
        });
    }

    /**
     * Regenerates subtitles for a video
     * @param {string} videoId - The ID of the video
     * @param {string} videoName - The name of the video
     * @returns {Promise} Promise resolving to regeneration result
     */
    async regenerate(videoId, videoName) {
        try {
            const result = await videoCRUDServiceInstance.regenerate(videoId);

            console.log('Regenerate result:', result);

            if (result && result.status === ReturnCodes.OK) {
                // Call tracking function if available and we have a videoId
                if (this.trackingFunction && videoId) {
                    console.log('Tracking regenerated video:', videoId, videoName);
                    this.trackingFunction(videoId, videoName + ' - Regenerate Subtitles', false);
                } else {
                    console.warn(
                        'Cannot track video: ',
                        !this.trackingFunction ? 'No tracking function available' : 'No video ID returned',
                    );
                }
                return true;
            } else {
                return false;
            }
        } catch (error) {
            console.error('Error in regeneration:', error);
            return false;
        }
    }

    /**
     * Re-compresses an existing video with -movflags +faststart.
     * @param {string} videoId - The ID of the video
     * @param {string} videoName - The name of the video
     * @returns {Promise<boolean>} Promise resolving to true on success
     */
    async recompress(videoId, videoName) {
        try {
            const result = await videoCRUDServiceInstance.recompress(videoId);
            console.log('Recompress result:', result);
            if (result && result.status === ReturnCodes.OK) {
                if (this.trackingFunction && videoId) {
                    this.trackingFunction(videoId, videoName + ' - Recompress', false);
                }
                return true;
            }
            return false;
        } catch (error) {
            console.error('Error in recompress:', error);
            return false;
        }
    }

    /**
     * Performs text-to-text translation for a video
     * @param {string} videoId - The ID of the video
     * @param {string} videoName - The name of the video
     * @param {string} sourceLanguage - The source language
     * @param {Array} targetLanguages - Array of target languages
     * @param {boolean} skipTranslation - Whether to skip translation
     * @returns {Promise} Promise resolving to translation result
     */
    async text2Text(videoId, videoName, sourceLanguage, targetLanguages, skipTranslation) {
        const userData = JSON.parse(localStorage.getItem('authUser'));

        try {
            const result = await videoCRUDServiceInstance.text2Text(
                videoId,
                sourceLanguage,
                targetLanguages,
                skipTranslation,
                userData.email,
                userData.access,
            );

            console.log('Text2Text result:', result);

            if (result && result.status === ReturnCodes.OK) {
                // Call tracking function if available and we have a videoId
                if (this.trackingFunction && videoId) {
                    console.log('Tracking text2text for video:', videoId, videoName);
                    this.trackingFunction(videoId, videoName + ' - Text 2 Text Translation', skipTranslation);
                } else {
                    console.warn(
                        'Cannot track video: ',
                        !this.trackingFunction ? 'No tracking function available' : 'No video ID returned',
                    );
                }
                return true;
            } else {
                return false;
            }
        } catch (error) {
            console.error('Error in text2Text:', error);
            return false;
        }
    }

    /**
     * Handles video upload with subtitles and automatic text-to-text translation
     * @param {string} linkType - The type of link (GOOGLE_DRIVE, YOUTUBE, LOCAL_FILE)
     * @param {Object} params - Upload parameters
     * @returns {Promise} Promise resolving to upload result
     */
    async uploadVideoWithSubtitles(linkType, params) {
        const {
            setFileUploading,
            googleLinkURL,
            youtubeLinkURL,
            videoName,
            folderPath,
            isCompressed,
            subtitleFile,
            sourceLanguage,
            targetLanguages,
            setOpen,
            onSuccess,
            selectedFile,
        } = params;

        setFileUploading(true);
        let url = '';

        if (linkType === 'GOOGLE_DRIVE') {
            url = googleLinkURL;
            linkType = 'gdrive';
        } else if (linkType === 'YOUTUBE') {
            url = youtubeLinkURL;
            linkType = 'youtube';
        } else if (linkType === 'LOCAL_FILE') {
            linkType = 'local';
            try {
                url = await localVideoUploadServiceInstance.uploadFile(selectedFile);
                console.log('Local file upload response:', url);
                url = url?.data;
                // url?.data is the server-stored path (e.g. filename_abc12.mp4)
                // Keep videoName as the user-provided name for readability at the AI server
            } catch (error) {
                console.error('Error uploading local file:', error);
                setFileUploading(false);
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Failed to upload local video file',
                });
                return;
            }
        }

        try {
            if (videoName.length === 0) {
                setFileUploading(false);
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Please enter a video name',
                });
                return;
            }

            // Validate that a subtitle file is provided
            if (!subtitleFile) {
                setFileUploading(false);
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Please select a subtitle file',
                });
                return;
            }

            // Validate subtitle file format
            const fileExtension = subtitleFile.name.split('.').pop().toLowerCase();
            if (fileExtension !== 'srt' && fileExtension !== 'vtt') {
                setFileUploading(false);
                Swal.fire({
                    icon: 'error',
                    title: 'Invalid File Format',
                    text: 'Please upload an SRT or VTT subtitle file',
                });
                return;
            }

            const userData = JSON.parse(localStorage.getItem('authUser'));

            // Use the VideoCRUDService method that calls the backend createWithSubtitles endpoint
            const result = await videoCRUDServiceInstance.uploadVideoWithSubtitles(
                linkType,
                url,
                userData,
                videoName,
                folderPath,
                subtitleFile,
                sourceLanguage,
                targetLanguages,
                isCompressed,
            );

            setFileUploading(false);
            if (setOpen) setOpen(false);

            // Show success message
            const targetLanguageCount = targetLanguages ? targetLanguages.split(',').length : 0;
            const translationMessage =
                targetLanguageCount > 0
                    ? `The translation to ${targetLanguageCount} language(s) is now being processed.<br><br>`
                    : '';

            Swal.fire({
                icon: 'success',
                title: 'Video Uploaded Successfully',
                html: `Your video <strong>${videoName}</strong> has been uploaded with subtitles.<br><br>
         ${translationMessage}
         You can track its progress in the toast notifications in the bottom right corner.`,
            });

            // Call onSuccess callback if provided
            if (onSuccess) {
                onSuccess(result);
            }

            // Call tracking function if available
            if (this.trackingFunction && result.id) {
                this.trackingFunction(result.id, videoName + ' - Upload with Subtitles', false);
            }

            return result;
        } catch (error) {
            setFileUploading(false);
            console.error('Error in uploadVideoWithSubtitles:', error);

            Swal.fire({
                icon: 'error',
                title: 'Upload Failed',
                text: error.response?.data?.message || 'An error occurred during upload',
            });

            return null;
        }
    }

    async generateVoiceOver(videoId, videoName, sourceLanguage, targetLanguages, skipAI = false) {
        try {
            // Call the backend API to start VO script generation
            const result = await videoCRUDServiceInstance.generateVoiceOver(videoId, sourceLanguage, targetLanguages, skipAI);

            console.log('VO script generation result:', result);

            if (result && result.status === ReturnCodes.OK) {
                // Call tracking function if available and we have a videoId
                if (this.trackingFunction && videoId) {
                    console.log('Tracking VO script generation for video:', videoId, videoName);
                    this.trackingFunction(videoId, videoName + ' - VO script Generation');
                } else {
                    console.warn(
                        'Cannot track video: ',
                        !this.trackingFunction ? 'No tracking function available' : 'No video ID returned',
                    );
                }
                return true;
            } else {
                console.error('Error in VO script generation:', result);
                return false;
            }
        } catch (error) {
            console.error('Error in generateVoiceOver:', error);
            return false;
        }
    }
}

const videoUploadServiceInstance = new VideoUploadService();
export default videoUploadServiceInstance;
